import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { icon } from '../lib/icons';
import { cardPresets, type CardTheme } from '../lib/card-presets';
import { telHref, mailHref, mapHref, urlHref, urlLabel, rowIcon, rowHref, socialLabel } from '../lib/helpers';
import { t, LocaleController } from '../lib/i18n';
import type { CardExtras, Contact, ExtraLink } from '../lib/collected';

/**
 * <lead-card> — Card v2. Front carries ALL contact info (each row taps through to
 * the native dialer / mail / maps / browser); the back carries only what the
 * owner put there (removed 2026-08-23: the send-this-card capture field — the
 * share surface, QR and OS share sheet already cover sending, and a form on
 * the card competed with the card): a bold
 * pitch, a scan-to-save QR, and a "swap cards" email capture. Flippable (tap the
 * front). Standard credit-card ratio (1.586). Themed entirely via --card-* tokens
 * — see {@link cardPresets}.
 */
@customElement('lead-card')
export class LeadCard extends LitElement {
  @property() name?: string;
  // Re-renders this element when the interface language changes.
  readonly i18n = new LocaleController(this);
  // `title` is a reserved HTMLElement property (typed `string`, non-optional), so
  // this reactive property must match it — default to '' rather than string|undefined.
  @property() title = '';
  // Which department, on a company card. Rides the title line — one line of
  // small print, not a second row the fixed card face has no room for.
  @property() department = '';
  @property() company?: string;
  @property() monogram?: string;
  @property() phone?: string;
  @property() email?: string;
  @property() address?: string;
  @property() website?: string;
  @property() theme?: CardTheme;
  @property() tagline?: string;
  /** The company that issued this card, when it is an employee card. A
   *  verified mark appears only when the company proved its domain AND this
   *  card's address is on it — so the mark means "this really is from them",
   *  not merely "they typed a company name". */
  @property() orgName = '';
  @property({ type: Boolean }) orgVerified = false;
  // The event a GUEST badge belongs to. When set, the brand line shows the
  // guest chip and NEVER the verified mark — a badge asserts attendance, not
  // employment, and the two must be impossible to confuse at a glance.
  @property() eventName = '';
  /** The owner's headshot as a data URL. When present it replaces the monogram
   *  badge on both faces; without one the monogram is the fallback. */
  @property() photo = '';
  /** A picture that replaces the card's designed back — the artwork many people
   *  already have printed on their physical card. When set, the back is just
   *  this image plus the flip control; the FRONT is unaffected. */
  @property() backImage = '';
  /** This card's public URL (https://<domain>/<publicId>). Enables the back-of-
   *  card "send my card" field — sending this URL to a phone/email/etc. */
  @property() url = '';
  // Optional raw `--card-*` override (e.g. an AI-generated theme). When set it
  // replaces the named preset — this is how the creator's live preview applies
  // a theming-proxy result that satisfies the --card-* contract.
  @property() styleVars = '';
  /** Labelled links + promo, shown on the DESIGNED back only. The front is
   *  untouched by any amount of extras — that promise (T-1004) is test-pinned —
   *  and a static backImage wins over them: the owner chose a picture. */
  @property({ attribute: false }) extras: CardExtras | null = null;
  @property({ attribute: false }) extraPhones: Contact[] = [];
  @property({ attribute: false }) extraEmails: Contact[] = [];
  /** Social/profile links, keyed by network (the server's closed allow-list).
   *  `website` is excluded here — it has its own slot on the FRONT. */
  @property({ attribute: false }) socials: Record<string, string> | null = null;
  @property({ type: Boolean, reflect: true }) flipped = false;

  static styles = css`
    :host{ display:block; transition:transform var(--dur-2,180ms) var(--ease-standard,cubic-bezier(.2,0,0,1)); }
    :host(:hover){ transform:translateY(-4px); }
    :host(:active){ transform:translateY(-1px); }
    .scene{ position:relative; width:100%; aspect-ratio:1.586/1; }
    .card{ position:absolute; inset:0; transition:transform var(--dur-4,380ms) var(--ease-emphasized,cubic-bezier(.05,.7,.1,1)); }
    :host([flipped]) .card{ transform:rotateY(180deg); }
    .face{ position:absolute; inset:0; border-radius:var(--card-radius,20px); background:var(--card-bg,#16181d); color:var(--card-fg,#f4f5f7);
      border:1px solid var(--card-border,rgba(255,255,255,.09)); box-shadow:var(--card-shadow,0 18px 40px -12px rgba(0,0,0,.55));
      font-family:var(--card-font,system-ui,sans-serif); transition:opacity 0ms linear 190ms, box-shadow var(--dur-3,260ms) var(--ease-standard,cubic-bezier(.2,0,0,1));
      /* A card is a physical object: nothing may sit outside its edge. This is
         the backstop, not the mechanism — the fields clamp themselves below, and
         if any future content forgets to, it gets clipped at the rounded corner
         rather than spilling across the page. */
      overflow:hidden; }
    /* Only the visible face is interactive — the hidden one (later in DOM, so on
       top) must not swallow clicks meant for the shown face. */
    .front{ opacity:1; } .back{ transform:rotateY(180deg); opacity:0; pointer-events:none; }
    :host(:hover) .face{ box-shadow:var(--card-shadow,0 18px 40px -12px rgba(0,0,0,.55)), 0 22px 38px -16px rgba(0,0,0,.5); }
    :host([flipped]) .front{ opacity:0; pointer-events:none; } :host([flipped]) .back{ opacity:1; pointer-events:auto; }
    .sheen,.scrim{ position:absolute; inset:0; pointer-events:none; border-radius:inherit; }
    .sheen{ background:var(--card-sheen,none); } .scrim{ background:var(--card-scrim,none); }
    .wrap{ position:relative; z-index:1; height:100%; box-sizing:border-box; padding:14px 15px; display:flex; flex-direction:column; }
    .top{ display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .brand{ display:flex; align-items:center; gap:10px; min-width:0; }
    .mono{ width:34px; height:34px; flex:none; border-radius:var(--card-mono-radius,10px); display:flex; align-items:center; justify-content:center;
      background:var(--card-mono-bg,#5a57e6); color:var(--card-mono-fg,#fff); font-family:var(--card-mono-font,var(--card-name-font,inherit)); font-weight:600; font-size:16px; line-height:1;
      box-shadow:var(--card-mono-shadow,none); overflow:hidden; }
    /* With a photo the badge becomes the card's visual anchor: bigger, round,
       and unpainted (the image supplies its own colour). */
    .mono.haspic{ width:44px; height:44px; border-radius:var(--card-photo-radius,999px); background:none;
      box-shadow:inset 0 0 0 1.5px var(--card-border,rgba(255,255,255,.25)); }
    .mono.lg.haspic{ width:52px; height:52px; }
    .mono img{ width:100%; height:100%; object-fit:cover; display:block; }
    .company{ font-size:11px; font-weight:600; letter-spacing:calc(.13em * var(--track, 1)); text-transform:uppercase; color:var(--card-fg-muted,#c6cad3); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    /* The verified mark sits with the company name because that is the claim it
       vouches for — not next to the person's name, which it says nothing about. */
    .vmark{ display:inline-flex; align-items:center; margin-left:5px; color:var(--card-contactless,var(--card-accent,#8481f7)); vertical-align:-2px; }
    .guest{ margin-left:6px; font-size:10px; letter-spacing:.02em; opacity:.75; font-weight:500;
      border:1px solid currentColor; border-radius:999px; padding:1px 7px; vertical-align:1px; }
    .vmark .ico{ font-size:13px; }
    .flipbtn{ width:30px; height:30px; flex:none; border:none; cursor:pointer; border-radius:999px; display:flex; align-items:center; justify-content:center;
      background:var(--card-flip-bg,rgba(255,255,255,.16)); color:var(--card-fg,#fff); box-shadow:inset 0 0 0 1px var(--card-border,rgba(255,255,255,.2)); transition:transform var(--dur-2,180ms) var(--ease-standard); }
    .flipbtn:hover{ transform:scale(1.08); }
    .flipbtn .ico{ font-size:17px; }
    .idblock{ margin:7px 0 5px; }
    .name{ font-family:var(--card-name-font,inherit); font-weight:var(--card-name-weight,600); font-size:18px; letter-spacing:var(--card-name-tracking,-.01em); line-height:1.05; color:var(--card-fg); }
    .ttl{ font-size:12.5px; color:var(--card-fg-muted,#9aa0ac); margin-top:5px; }
    /* minmax(0,1fr), not 1fr: a grid track defaults to min-content, so a long
       address widens the column instead of being clipped by it — which is how
       an email ran hundreds of pixels off the side of the card. The same
       min-width:0 is needed on the row and on the value, because a flex or grid
       ITEM will not shrink below its content either, and without that the
       overflow:hidden below never gets the chance to do anything. */
    .fields{ margin-top:auto; display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr);
      gap:9px 12px; min-width:0; }
    /* No pill, no border: the rows read as printed card text, and the GLYPH
       carries the affordance instead (accent-colored at rest, physical under
       the finger). Chrome around every row made four buttons out of what a
       card states in type. */
    .fld{ display:flex; align-items:center; gap:8px; min-width:0; min-height:32px;
      padding:0 2px; border-radius:8px; text-decoration:none; color:var(--card-fg); }
    .fld .ico{ flex:none; font-size:19px; color:var(--card-contactless,var(--card-accent,#8481f7)); }
    /* The physics live on a wrapper span, NOT the svg: the icon's one-shot
       entrance animation keeps applying its final keyframe (fill-mode both),
       so a transform transitioned on the svg itself would be overridden
       forever. The whole ROW is still the pointer target — a number is tapped
       by its text far more often than by its 19px glyph. */
    .ig{ display:inline-flex; flex:none; }
    @media (prefers-reduced-motion: no-preference) {
      .ig{ transition:transform .45s cubic-bezier(.3,1.9,.45,.94); will-change:transform; }
      .fld:hover .ig, .xrow:hover .ig{ transform:translateY(-1.5px) scale(1.22); transition-duration:.28s; }
      /* Press squashes fast; release springs back through the overshoot curve
         above — squash → bounce is what says "this will act" without a button. */
      .fld:active .ig, .xrow:active .ig{ transform:scale(.78); transition-duration:70ms; }
    }
    .fld:hover .v, .xrow:hover .v{ color:var(--card-fg); }
    .fld:focus-visible, .xrow:focus-visible{ outline:2px solid var(--card-accent,#8481f7); outline-offset:2px; }
    .fld .v{ font-size:12px; color:var(--card-fg-muted,#cfd3db); white-space:nowrap;
      overflow:hidden; text-overflow:ellipsis; flex:1; min-width:0; }
    .backtop{ display:flex; align-items:center; justify-content:space-between; }
    /* flex:1 1 0 is what makes this scroll: without a bounded height a flex
       child sizes to its content and pushes past the fixed-ratio face. */
    .xtr{ flex:1 1 0; min-height:0; overflow-y:auto; display:flex; flex-direction:column; gap:6px; margin-top:8px;
      scrollbar-width:thin; }
    .xrow{ display:flex; align-items:center; gap:8px; min-height:30px; flex:none; padding:0 2px;
      border-radius:8px; text-decoration:none; color:var(--card-fg); }
    .xrow .ico{ flex:none; font-size:16px; color:var(--card-contactless,var(--card-accent,#8481f7)); }
    .xrow .v{ font-size:12px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0; }
    .xpromo{ display:flex; flex-direction:column; gap:4px; padding:8px 10px; border-radius:12px; flex:none;
      background:var(--card-flip-bg,rgba(255,255,255,.08)); }
    .xph{ font-weight:700; font-size:12.5px; }
    .xpb{ font-size:11.5px; color:var(--card-fg-muted,#cfd3db); }
    .mono.lg{ width:36px; height:36px; font-size:16px; border-radius:11px; }
    .brow{ display:flex; align-items:flex-start; gap:12px; margin:8px 0 0; }
    .pitch{ flex:1; min-width:0; }
    .headline{ font-family:var(--card-name-font,inherit); font-weight:var(--card-name-weight,700); font-size:20px; line-height:1.02; letter-spacing:calc(-.01em * var(--track, 1)); color:var(--card-fg); }
    .tagline{ font-size:11px; line-height:1.35; color:var(--card-fg-muted); margin-top:5px; }
    .qrblock{ flex:none; display:flex; flex-direction:column; align-items:center; gap:3px; }
    .qrtile{ width:50px; height:50px; border-radius:12px; display:flex; align-items:center; justify-content:center; background:var(--card-save-bg,#fff); color:var(--card-save-fg,#111); }
    .qrtile .ico{ font-size:32px; }
    .qrcap{ font-size:8px; letter-spacing:calc(.12em * var(--track, 1)); text-transform:uppercase; color:var(--card-fg-faint); }
    .send{ width:30px; height:30px; flex:none; border:none; cursor:pointer; border-radius:999px; display:flex; align-items:center; justify-content:center; background:var(--card-save-bg,var(--card-accent,#8481f7)); color:var(--card-save-fg,#fff); }
    .send .ico{ font-size:17px; }

    /* A static back: the picture IS the back. Everything the designed back
       carries is dropped rather than laid over the top, because text on top of
       somebody's artwork is unreadable as often as not. The flip control stays,
       or the card becomes a trap. */
    .backpic{ position:absolute; inset:0; border-radius:inherit; overflow:hidden; }
    .backpic img{ width:100%; height:100%; object-fit:cover; display:block; }
    .backflip{ position:absolute; top:10px; right:10px; z-index:2; }
  `;

  /** True while the user has an active text selection inside the card, so a
   *  click that ends a drag-select doesn't also flip the card. */
  private hasSelection(): boolean {
    const root = this.shadowRoot as (ShadowRoot & { getSelection?: () => Selection | null }) | null;
    const sel = root?.getSelection?.() ?? (typeof window !== 'undefined' ? window.getSelection() : null);
    return !!sel && sel.toString().trim().length > 0;
  }

  /** Toggle the card. Tapping a face flips it (front⇄back); selecting text does
   *  not. The explicit flip buttons pass `force` so they always flip. */
  private flip(e?: Event, force = false) {
    if (!force && this.hasSelection()) return;
    if (e) { e.preventDefault(); e.stopPropagation(); }
    this.flipped = !this.flipped;
  }

  private get mono() { return this.monogram || (this.name ? this.name[0] : 'A'); }

  /** The identity badge: the headshot when there is one, else the monogram.
   *  `lg` is the larger variant used on the back. */
  private badge(lg = false) {
    const cls = `mono${lg ? ' lg' : ''}${this.photo ? ' haspic' : ''}`;
    return html`<div class=${cls}>
      ${this.photo
        ? html`<img src=${this.photo} alt=${this.name ? `${this.name}’s photo` : 'Card photo'} />`
        : this.mono}
    </div>`;
  }


  /** Whether this card carries anything for the back's extras region. When it
   *  does, the back's pitch + QR decoration yields — the face is 221px tall on
   *  a phone and something must: measured live, extras got a 15px sliver
   *  otherwise. The QR block is an icon, not a scannable code, so real
   *  contact rows outrank it. */
  /** Extras on the designed back: labelled links, promo, extra contacts. The
   *  face keeps its fixed ratio — this region scrolls inside it instead of
   *  stretching it, which is what kept extras off the card entirely before. */
  private renderBackExtras() {
    const x = this.extras;
    const links = x?.links ?? [];
    const promo = x?.promo;
    // `website` lives on the front; the rest are profiles and belong here.
    const socials = Object.entries(this.socials ?? {}).filter(([k, v]) => k !== 'website' && v);
    if (!links.length && !promo && !socials.length
      && !this.extraPhones.length && !this.extraEmails.length) return nothing;
    return html`
      <div class="xtr" @click=${(e: Event) => e.stopPropagation()}>
        ${promo?.headline || promo?.body ? html`
          <div class="xpromo">
            ${promo.headline ? html`<div class="xph">${promo.headline}</div>` : nothing}
            ${promo.body ? html`<div class="xpb">${promo.body}</div>` : nothing}
            ${promo.url ? html`<a class="xrow" href=${urlHref(promo.url)} target="_blank" rel="noopener"><span class="ig">${icon('link')}</span><span class="v">${promo.cta || urlLabel(promo.url)}</span></a>` : nothing}
          </div>` : nothing}
        ${links.map((l: ExtraLink) => html`<a class="xrow" href=${rowHref(l.url)} target="_blank" rel="noopener"><span class="ig">${icon(rowIcon(l.url))}</span><span class="v">${l.label || urlLabel(l.url)}</span></a>`)}
        ${socials.map(([k, v]) => html`<a class="xrow" href=${urlHref(v)} target="_blank" rel="noopener"><span class="ig">${icon('social')}</span><span class="v">${socialLabel(k)}</span></a>`)}
        ${this.extraPhones.map((c) => html`<a class="xrow" href=${telHref(c.value)}><span class="ig">${icon('call')}</span><span class="v">${c.label ? c.label + ' · ' : ''}${c.value}</span></a>`)}
        ${this.extraEmails.map((c) => html`<a class="xrow" href=${mailHref(c.value)}><span class="ig">${icon('mail')}</span><span class="v">${c.label ? c.label + ' · ' : ''}${c.value}</span></a>`)}
      </div>`;
  }

  render() {
    return html`
    <div class="scene" style=${this.styleVars || cardPresets[this.theme as CardTheme] || ''}>
      <div class="card">
        <div class="face front" @click=${this.flip} style="cursor:pointer">
          <div class="sheen"></div><div class="scrim"></div>
          <div class="wrap">
            <div class="top">
              <div class="brand">${this.badge()}<span class="company">${this.company}${this.eventName
                ? html`<span class="guest">${t('card.guest')} · ${this.eventName}</span>`
                : this.orgVerified
                  ? html`<span class="vmark" title=${t('card.verifiedBy', { name: this.orgName || this.company || '' })}
                      aria-label=${t('card.verifiedCo')}>${icon('how_to_reg')}</span>`
                  : nothing}</span></div>
              <button class="flipbtn" @click=${(e: Event) => this.flip(e, true)} aria-label=${t('card.flip')}>${icon('flip_to_back')}</button>
            </div>
            <div class="idblock"><div class="name">${this.name}</div><div class="ttl">${[this.title, this.department].filter(Boolean).join(' \u00b7 ')}</div></div>
            <div class="fields" @click=${(e: Event) => e.stopPropagation()}>
              ${this.phone ? html`<a class="fld" href=${telHref(this.phone)}><span class="ig">${icon('call')}</span><span class="v">${this.phone}</span></a>` : nothing}
              ${this.email ? html`<a class="fld" href=${mailHref(this.email)}><span class="ig">${icon('mail')}</span><span class="v">${this.email}</span></a>` : nothing}
              ${this.website ? html`<a class="fld" href=${urlHref(this.website)} target="_blank" rel="noopener"><span class="ig">${icon('language')}</span><span class="v">${urlLabel(this.website)}</span></a>` : nothing}
              ${this.address ? html`<a class="fld" href=${mapHref(this.address)} target="_blank" rel="noopener"><span class="ig">${icon('place')}</span><span class="v">${this.address}</span></a>` : nothing}
            </div>
          </div>
        </div>
        <div class="face back" @click=${this.flip} style="cursor:pointer">
          <div class="sheen"></div><div class="scrim"></div>
          ${this.backImage && this.flipped ? html`
            <div class="backpic"><img src=${this.backImage} alt="" /></div>
            <button class="flipbtn backflip" @click=${(e: Event) => this.flip(e, true)}
              aria-label=${t('card.flip')}>${icon('flip_to_front')}</button>
          ` : nothing}
          <div class="wrap">
            ${this.flipped && !this.backImage ? html`
            <div class="backtop">
              ${this.badge(true)}
              <button class="flipbtn" @click=${(e: Event) => this.flip(e, true)} aria-label=${t('card.flip')}>${icon('flip_to_front')}</button>
            </div>
            ${/* Only what the OWNER wrote, like the front: a tagline renders
                the moment it is typed, and an empty back is BLANK — no stock
                headline, no placeholder copy standing in for content
                (operator decision, 2026-08-23). */
            this.tagline ? html`
            <div class="brow">
              <div class="pitch">
                <div class="tagline">${this.tagline}</div>
              </div>
              <div class="qrblock"><div class="qrtile">${icon('qr_code_2')}</div><span class="qrcap">${t('card.scan')}</span></div>
            </div>` : nothing}
            ${this.renderBackExtras()}
            ` : nothing}
          </div>
        </div>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lead-card': LeadCard; }
}
